import { Link } from 'react-router-dom';
import { ArrowRight, Bell, Calendar, Plug, Shield, Users } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useOrg } from '../contexts/OrgContext';
import {
  AppShell,
  Badge,
  Card,
  CardBody,
  Container,
  PageHeader,
  Row,
  Stack,
} from '../components/ui';

export default function HomePage() {
  const { profile } = useAuth();
  const { role } = useOrg();
  const greetingName = profile?.name?.split(' ')[0] || 'there';

  return (
    <AppShell>
      <Container width="wide">
        <PageHeader
          title={`Welcome back, ${greetingName}`}
          subtitle="On-call notification, escalation, and shift management."
          actions={
            role === 'admin' ? (
              <Link
                to="/settings/team"
                className="ds-button ds-button--secondary ds-button--sm"
              >
                Team settings
                <ArrowRight size={14} strokeWidth={1.75} />
              </Link>
            ) : null
          }
        />

        <Stack gap={4}>
          <Stack gap={3}>
            <Row gap={2} align="center">
              <h2 style={{ fontSize: 'var(--fs-lg)' }}>Coming soon</h2>
              <Badge tone="accent" dot>
                In development
              </Badge>
            </Row>
            <p className="ds-text-muted">
              These are the surfaces being built next. Each card lights up as we ship its chunk.
            </p>
          </Stack>

          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))',
              gap: 'var(--space-4)',
            }}
          >
            <FeatureCard
              icon={<Shield size={18} strokeWidth={1.75} />}
              title="Services"
              description="Define monitored services, tier them, link integrations."
              status="ready"
              href="/services"
            />
            <FeatureCard
              icon={<Plug size={18} strokeWidth={1.75} />}
              title="Integrations"
              description="Wire up Datadog, New Relic, Sentry, and friends — fixed-service or rule-based routing."
              status="ready"
              href="/integrations"
              hidden={role !== 'admin'}
            />
            <FeatureCard
              icon={<Calendar size={18} strokeWidth={1.75} />}
              title="Escalation policies"
              description="Who gets paged, in what order, when a service incident is raised."
              status="ready"
              href="/escalation-policies"
              hidden={role !== 'admin'}
            />
            <FeatureCard
              icon={<Bell size={18} strokeWidth={1.75} />}
              title="Incidents"
              description="Triggered, acknowledged, resolved — with timers and an audit log."
              status="ready"
              href="/incidents"
            />
            <FeatureCard
              icon={<Users size={18} strokeWidth={1.75} />}
              title="Team"
              description="Invite members, manage roles."
              status="ready"
              href="/settings/team"
              hidden={role !== 'admin'}
            />
          </div>
        </Stack>
      </Container>
    </AppShell>
  );
}

type FeatureStatus = 'ready' | 'next' | 'planned';

function FeatureCard({
  icon,
  title,
  description,
  status,
  href,
  hidden = false,
}: {
  icon: React.ReactNode;
  title: string;
  description: string;
  status: FeatureStatus;
  href?: string;
  hidden?: boolean;
}) {
  if (hidden) return null;
  const tone = status === 'ready' ? 'success' : status === 'next' ? 'accent' : 'neutral';
  const label = status === 'ready' ? 'Ready' : status === 'next' ? 'Next chunk' : 'Planned';

  const inner = (
    <Card>
      <CardBody>
        <Stack gap={3}>
          <Row gap={2} justify="between" align="start">
            <div
              style={{
                color: 'var(--color-accent-soft-text)',
                background: 'var(--color-accent-soft)',
                width: 36,
                height: 36,
                display: 'grid',
                placeItems: 'center',
                borderRadius: 'var(--radius-md)',
              }}
            >
              {icon}
            </div>
            <Badge tone={tone} dot>
              {label}
            </Badge>
          </Row>
          <Stack gap={1}>
            <div style={{ fontSize: 'var(--fs-md)', fontWeight: 'var(--fw-semibold)' }}>{title}</div>
            <div className="ds-text-muted ds-text-sm">{description}</div>
          </Stack>
        </Stack>
      </CardBody>
    </Card>
  );

  return href ? (
    <Link to={href} style={{ color: 'inherit', textDecoration: 'none' }}>
      {inner}
    </Link>
  ) : (
    inner
  );
}
